import React, { useCallback } from 'react';
import { Product } from '@wconcept/wconcept-common/models/display/product';
import useHistoryRouter from '@hooks/useLinkTo';
import LoaderSpinner from '@wconcept/wconcept-ui-renewal/src/components/atoms/LoaderSpinner';
import Pagination from '@wconcept/wconcept-ui-renewal/src/components/molecules/_indicator/Pagination';
import PrdcUnitItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/PrdcUnitItem';
import { getExclusiveEmblemProp } from '@utils/productExclusiveUtil';
import ProductList from '@wconcept/wconcept-ui-renewal/src/components/organisms/_list/ProductList';
import InfiniteScroll from 'react-infinite-scroll-component';
import MolocoRecProductList from '@components/pages/common/MolocoRecProductList';
import EmptyBox from '@wconcept/wconcept-ui-renewal/src/components/molecules/_empty/EmptyBox';
import { DesktopProductListProps } from '../types';

const DesktopProductList = React.memo<DesktopProductListProps>(
  ({
    products,
    isMobile,
    assistData,
    isPriority,
    myHeartList,
    onClickProduct,
    onClickHeart,
    isCustomAd,
    molocoAdProductsResult,
    totalElements,
    appInfo,
    onClickMolocoProductItem,
    recommendBgColor,
    hasNextPage,
    fetchNextPage,
    tabSelectedValue,
    page,
    perPage,
    totalPages,
    onPageChange,
    hideLastPageButton,
    selectedFilters,
    emptyText,
    onReset,
    onClickViewStylVdClick,
    showExclusiveEmblem,
  }: DesktopProductListProps) => {
    const { linkTo } = useHistoryRouter();

    const onClickViewStylVd = useCallback(
      async (product: Product, index: number) => {
        onClickViewStylVdClick?.(product, index);
        if (product.shortFormInfo?.landingUrl) {
          linkTo.push({
            url: product.shortFormInfo?.landingUrl,
          });
        }
      },
      [linkTo, onClickViewStylVdClick]
    );

    if (products.length === 0) {
      const hasSelectedFilter = selectedFilters && selectedFilters.length > 0;

      if (hasSelectedFilter) {
        return (
          <EmptyBox
            msg="필터 검색 결과가 없습니다.<br />선택한 필터를 변경해보세요."
            btnText="필터 재설정"
            onClick={onReset}
          />
        );
      }
      return <EmptyBox msg={emptyText || '상품이 없습니다.'} />;
    }

    return (
      <>
        <InfiniteScroll
          loader={isMobile ? <LoaderSpinner /> : undefined}
          hasMore={
            !!isMobile &&
            hasNextPage &&
            (tabSelectedValue === undefined || tabSelectedValue === 'product')
          }
          dataLength={products.length}
          next={fetchNextPage}
          scrollThreshold={0.7}
        >
          <ProductList>
            {products.map((v, i) => (
              <React.Fragment key={v.itemCd}>
                <PrdcUnitItem
                  isDesktop={!isMobile}
                  viewStylVdUrl={v.shortFormInfo?.landingUrl}
                  isLogin={assistData.isLogin}
                  priority={isPriority ? i < 6 : false}
                  itemCd={v.itemCd}
                  statusCd={v.statusCd}
                  finalPrice={v.finalPrice}
                  customerPrice={v.customerPrice}
                  finalDiscountRate={v.finalDiscountRate}
                  reviewScore={v.reviewScore}
                  reviewCnt={v.reviewCnt}
                  heartCnt={v.heartCnt}
                  reserveTag={v.reserveTag}
                  saleTag={v.saleTag}
                  infoTags={v.infoTags}
                  imageUrl={v.imageUrlMobile || v?.productImageUrl}
                  exclusive={getExclusiveEmblemProp(v, showExclusiveEmblem)}
                  brandCd={v.brandCd}
                  brandNameKr={v.brandNameKr}
                  itemNameSub={v.itemNameSub}
                  itemName={v.itemName}
                  myHeartItem={
                    v?.itemCd ? !!myHeartList?.product?.has(v?.itemCd) : false
                  }
                  todayDeliveryTag={v.todayDeliveryTag}
                  colors={v.colors}
                  onClick={({ heartCnt }) =>
                    onClickProduct({ ...v, heartCnt }, i)
                  }
                  onClickHeart={async (itemCd, isActive, heartCnt) =>
                    onClickHeart?.({ ...v, heartCnt }, isActive, i)
                  }
                  onClickViewStylVd={() => onClickViewStylVd?.(v, i)}
                />
                {isCustomAd &&
                molocoAdProductsResult &&
                totalElements > 5 &&
                i + 1 === molocoAdProductsResult?.webPosition &&
                isMobile ? (
                  <MolocoRecProductList
                    list={molocoAdProductsResult.items}
                    isNativeAppBoth={appInfo.isNativeAppBoth}
                    onClickItem={onClickMolocoProductItem}
                    bgColor={recommendBgColor}
                    showExclusiveEmblem={showExclusiveEmblem}
                    onClickHeart={async (item, isActive, index) =>
                      onClickHeart?.(item, isActive, index, 'moloco')
                    }
                  />
                ) : null}
              </React.Fragment>
            ))}
          </ProductList>
        </InfiniteScroll>
        {!isMobile && (
          <Pagination
            activePage={page || 1}
            itemsCountPerPage={perPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            hideLastPageButton={hideLastPageButton}
          />
        )}
      </>
    );
  }
);

DesktopProductList.displayName = 'DesktopProductList';

export default DesktopProductList;
